/**
 * Shared loading state.
 *
 * The globe is rendered with a real D3 orthographic projection over the
 * bundled `world-atlas/land-110m.json` topology, so continents look right
 * (matches the supplied "Globe Loader (standalone-src).html" reference).
 *
 * The wrapper is intentionally TRANSPARENT — earlier revisions painted a
 * warm beige (#e7e7ea) panel that overlapped the host page; we now let the
 * underlying surface show through.
 */

import { useEffect, useId, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { geoOrthographic, geoPath, geoGraticule } from 'd3-geo';
import { feature } from 'topojson-client';
import landTopo from 'world-atlas/land-110m.json';

/**
 * Pre-computed land features. The world-atlas topology is identical for every
 * mount, so derive the GeoJSON once at module load to avoid per-instance work.
 */
const LAND_FEATURES = feature(landTopo, landTopo.objects.land);

const SVG_SIZE = 200;          // viewBox dimension
const GLOBE_RADIUS = 62;       // px in the SVG coordinate system
const ROTATION_DEG_PER_SEC = 60;
const TILT_DEG = -15;          // matches the reference (slight northern lean)

/**
 * Animated globe (continents + graticule) inside the static disk + whirl rings.
 * Geometry is updated each animation frame via direct DOM `setAttribute('d', …)`
 * calls so React doesn't have to reconcile 60 times a second.
 */
const GlobeLoader = ({ size = 172 }) => {
  const graticuleRef = useRef(null);
  const landRef = useRef(null);
  // Unique clip-path id so multiple GlobeLoader instances on the same page
  // don't share one clipPath (which causes intermittent rendering glitches).
  const clipId = `aviatto-globe-clip-${useId().replace(/[:]/g, '')}`;

  useEffect(() => {
    const projection = geoOrthographic()
      .scale(GLOBE_RADIUS)
      .translate([SVG_SIZE / 2, SVG_SIZE / 2])
      .clipAngle(90)
      .rotate([0, TILT_DEG, 0]);
    const pathGenerator = geoPath(projection);
    const graticule = geoGraticule().step([20, 20]);

    let rafId = 0;
    let lambda = 0;
    let last = performance.now();

    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      lambda = (lambda + ROTATION_DEG_PER_SEC * dt) % 360;
      projection.rotate([lambda, TILT_DEG, 0]);

      const graticuleNode = graticuleRef.current;
      const landNode = landRef.current;
      if (graticuleNode) {
        graticuleNode.setAttribute('d', pathGenerator(graticule()) || '');
      }
      if (landNode) {
        landNode.setAttribute('d', pathGenerator(LAND_FEATURES) || '');
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        position: 'relative',
        animation: 'aviattoGlobeBreathe 3.6s ease-in-out infinite',
        '@keyframes aviattoGlobeBreathe': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.015)' },
        },
        '@keyframes aviattoGlobeSpin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        },
        '@keyframes aviattoGlobeSpinReverse': {
          from: { transform: 'rotate(360deg)' },
          to: { transform: 'rotate(0deg)' },
        },
      }}
    >
      {/* Whirl layer: orbital dashed rings, slow ticks, fast particle trail. */}
      <Box
        component="svg"
        viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          overflow: 'visible',
        }}
      >
        <g
          style={{ transformOrigin: '100px 100px', animation: 'aviattoGlobeSpin 3.2s linear infinite' }}
        >
          <circle cx="100" cy="100" r="92" fill="none" stroke="#1d1f20" strokeOpacity="0.55" strokeWidth="1" strokeDasharray="60 240" strokeLinecap="round" />
          <circle cx="100" cy="100" r="92" fill="none" stroke="#1d1f20" strokeOpacity="0.28" strokeWidth="1" strokeDasharray="18 40 6 220" strokeLinecap="round" />
        </g>

        <g
          style={{ transformOrigin: '100px 100px', animation: 'aviattoGlobeSpinReverse 5.6s linear infinite' }}
        >
          <circle cx="100" cy="100" r="84" fill="none" stroke="#1d1f20" strokeOpacity="0.35" strokeWidth="0.9" strokeDasharray="2 6" strokeLinecap="round" />
        </g>

        <g
          stroke="#1d1f20"
          strokeOpacity="0.5"
          strokeWidth="1"
          strokeLinecap="round"
          style={{ transformOrigin: '100px 100px', animation: 'aviattoGlobeSpin 8s linear infinite' }}
        >
          <line x1="100" y1="4" x2="100" y2="10" />
          <line x1="100" y1="190" x2="100" y2="196" />
          <line x1="4" y1="100" x2="10" y2="100" />
          <line x1="190" y1="100" x2="196" y2="100" />
        </g>

        <g style={{ transformOrigin: '100px 100px', animation: 'aviattoGlobeSpin 2.2s linear infinite' }}>
          <circle cx="100" cy="12" r="1.6" fill="#1d1f20" />
          <circle cx="100" cy="16" r="1.1" fill="#1d1f20" fillOpacity="0.6" />
          <circle cx="100" cy="20" r="0.8" fill="#1d1f20" fillOpacity="0.35" />
          <circle cx="100" cy="23" r="0.5" fill="#1d1f20" fillOpacity="0.2" />
        </g>
      </Box>

      {/* Globe layer: real continents + graticule, animated by D3 in useEffect. */}
      <Box
        component="svg"
        viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          overflow: 'visible',
        }}
      >
        <defs>
          <clipPath id={clipId}>
            <circle cx="100" cy="100" r={GLOBE_RADIUS - 0.5} />
          </clipPath>
        </defs>
        {/* Globe disk: TRANSPARENT fill so the host page surface (white or any
            other) shows through. Earlier revisions used a warm beige
            (#e7e7ea) "vintage paper map" colour, which painted a beige
            circle on top of every page that mounted the loader. The dark
            continents (drawn below) still read clearly against any light
            surface, and the rim circle drawn after the continents keeps the
            edge crisp without needing a solid sphere underneath. */}
        <circle cx="100" cy="100" r={GLOBE_RADIUS} fill="transparent" stroke="#1d1f20" strokeWidth="1.25" />
        <g clipPath={`url(#${clipId})`}>
          <path
            ref={graticuleRef}
            fill="none"
            stroke="#1d1f20"
            strokeOpacity="0.22"
            strokeWidth="0.5"
          />
          <path
            ref={landRef}
            fill="#1d1f20"
            fillOpacity="0.82"
            stroke="#1d1f20"
            strokeWidth="0.35"
            strokeLinejoin="round"
          />
        </g>
        {/* Crisp rim drawn on top so continents never overdraw the edge. */}
        <circle cx="100" cy="100" r={GLOBE_RADIUS} fill="none" stroke="#1d1f20" strokeWidth="1.25" />
      </Box>
    </Box>
  );
};

/**
 * Public loader: transparent container so the host page surface shows through.
 * Pass `message` to caption the spinner. `minHeight` keeps suspense fallbacks
 * from collapsing the layout.
 */
const LoadingSpinner = ({ message = 'Loading...' }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: 2.25,
        bgcolor: 'transparent',
        color: '#1d1f20',
      }}
    >
      <GlobeLoader />
      {message ? (
        <Typography
          variant="body1"
          sx={{
            color: '#1d1f20',
            fontWeight: 700,
            letterSpacing: '0.02em',
            textAlign: 'center',
          }}
        >
          {message}
        </Typography>
      ) : null}
    </Box>
  );
};

export default LoadingSpinner;
